import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KeyRound, Pencil, Plus, Trash2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { relativeTime } from '../../lib/time';
import { Avatar, Spinner, toast } from '@vatman/ui';
import { errorText } from '../../i18n';
import { AgentBadge } from '../../ui/AgentBadge';
import { ConfirmDialog } from './parts';
import { CreatedToken, TokenForm, TokenRow, type ApiToken, type NewApiToken } from './ApiTokens';

/** An agent account (crates/server/src/api/agents.rs). */
interface AgentAccount {
  id: string;
  name: string;
  color: string;
  createdAt: number;
  createdBy: string | null;
  team: { id: string; name: string };
  tokens: ApiToken[];
}

interface AgentsRes {
  agents: AgentAccount[];
  /** Teams where the person may create agents. */
  teams: { id: string; name: string }[];
}

const key = ['agent-accounts'];

/**
 * Profile → AI agents → Agent accounts: a team's service accounts that people assign issues to,
 * with their tokens. Team owners and admins see and manage their teams' agents.
 */
export function AgentAccounts({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: key, queryFn: () => api.get<AgentsRes>('/agents'), enabled });
  const [name, setName] = useState('');
  const [team, setTeam] = useState('');
  const [tokenFor, setTokenFor] = useState<string | null>(null);
  const [created, setCreated] = useState<NewApiToken | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState<AgentAccount | null>(null);
  const [revoking, setRevoking] = useState<{ agent: AgentAccount; tok: ApiToken } | null>(null);
  const teams = list.data?.teams ?? [];
  const teamId = team || teams[0]?.id || '';

  const fail = (e: unknown) => toast(errorText(e instanceof ApiError ? e.code : 'internal'));
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const create = useMutation({
    mutationFn: () => api.post<AgentAccount>('/agents', { teamId, name: name.trim() }),
    onSuccess: (a) => {
      setName('');
      setTokenFor(a.id);
      toast(t('agentAccounts.created'));
      refresh();
    },
    onError: fail,
  });
  const rename = useMutation({
    mutationFn: (r: { id: string; name: string }) => api.patch(`/agents/${r.id}`, { name: r.name.trim() }),
    onSuccess: () => {
      setRenaming(null);
      refresh();
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/agents/${id}`),
    onSuccess: () => {
      toast(t('agentAccounts.removed'));
      refresh();
    },
    onError: fail,
  });
  const revoke = useMutation({
    mutationFn: (r: { agent: string; tok: string }) => api.del(`/agents/${r.agent}/tokens/${r.tok}`),
    onSuccess: refresh,
    onError: fail,
  });

  if (created) return <CreatedToken token={created} onDone={() => setCreated(null)} />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && teamId) create.mutate();
  };

  return (
    <div className="agent-accounts">
      <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-3)' }}>
        {t('agentAccounts.intro')}
      </p>
      {list.isLoading ? (
        <Spinner />
      ) : !teams.length ? (
        <div className="muted">{t('agentAccounts.noTeams')}</div>
      ) : (
        <form className="tokens-form" onSubmit={submit}>
          <label className="field" style={{ flex: 2 }}>
            <span>{t('agentAccounts.name')}</span>
            <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder={t('agentAccounts.namePlaceholder')} />
          </label>
          {teams.length > 1 && (
            <label className="field" style={{ flex: 1.5 }}>
              <span>{t('agentAccounts.team')}</span>
              <select className="input" value={teamId} onChange={(e) => setTeam(e.target.value)}>
                {teams.map((tm) => (
                  <option key={tm.id} value={tm.id}>
                    {tm.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button className="btn primary" disabled={create.isPending || !name.trim()}>
            <Plus size={16} /> {t('agentAccounts.create')}
          </button>
        </form>
      )}

      {list.data && !list.data.agents.length && teams.length > 0 && <div className="muted">{t('agentAccounts.empty')}</div>}
      {list.data?.agents.map((a) => (
        <div key={a.id} className="agent-card">
          <div className="agent-head">
            <Avatar name={a.name} color={a.color} size={30} />
            <div style={{ minWidth: 0, flex: 1 }}>
              {renaming?.id === a.id ? (
                <form
                  className="agent-rename"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (renaming.name.trim()) rename.mutate(renaming);
                  }}
                >
                  <input className="input" autoFocus value={renaming.name} maxLength={60} onChange={(e) => setRenaming({ id: a.id, name: e.target.value })} onKeyDown={(e) => e.key === 'Escape' && setRenaming(null)} />
                  <button className="btn sm primary" disabled={rename.isPending || !renaming.name.trim()}>
                    {t('common.save')}
                  </button>
                </form>
              ) : (
                <div className="tokens-name">
                  {a.name}
                  <AgentBadge />
                </div>
              )}
              <div className="muted tokens-meta">
                {a.team.name}
                {a.createdBy ? ` · ${t('agentAccounts.createdBy', { name: a.createdBy })}` : ''} · {relativeTime(a.createdAt)}
              </div>
            </div>
            <div className="agent-actions">
              <button className="btn sm" onClick={() => setTokenFor(tokenFor === a.id ? null : a.id)}>
                <KeyRound size={15} /> {t('agentAccounts.newToken')}
              </button>
              <button className="icon-btn sm" title={t('agentAccounts.rename')} aria-label={t('agentAccounts.rename')} onClick={() => setRenaming({ id: a.id, name: a.name })}>
                <Pencil size={15} />
              </button>
              <button className="icon-btn sm danger" title={t('agentAccounts.remove')} aria-label={t('agentAccounts.remove')} onClick={() => setRemoving(a)}>
                <Trash2 size={15} />
              </button>
            </div>
          </div>
          {tokenFor === a.id && (
            <TokenForm
              endpoint={`/agents/${a.id}/tokens`}
              invalidate={key}
              onCreated={(tok) => {
                setTokenFor(null);
                setCreated(tok);
              }}
            />
          )}
          {a.tokens.length ? (
            a.tokens.map((tok) => <TokenRow key={tok.id} tok={tok} onRevoke={() => setRevoking({ agent: a, tok })} />)
          ) : (
            <div className="muted tokens-meta">{t('agentAccounts.noTokens')}</div>
          )}
        </div>
      ))}

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(v) => !v && setRemoving(null)}
        title={t('agentAccounts.removeTitle')}
        text={t('agentAccounts.removeText', { name: removing?.name ?? '' })}
        confirmLabel={t('agentAccounts.remove')}
        onConfirm={() => removing && remove.mutate(removing.id)}
      />
      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(v) => !v && setRevoking(null)}
        title={t('tokens.revokeTitle')}
        text={t('tokens.revokeText', { name: revoking?.tok.name ?? '' })}
        confirmLabel={t('tokens.revoke')}
        onConfirm={() => revoking && revoke.mutate({ agent: revoking.agent.id, tok: revoking.tok.id })}
      />
    </div>
  );
}
